import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { updateHiringGoalTargets } from '@/api-clients/update-hiring-goal-targets';
import { useToaster } from '@/components/base/toaster';
import { Input } from '@/components/ui/input';
import { TargetDetails } from '@/components/ui/target-details';
import { useEditTargetsContext } from '@/contexts/edit-targets-context';
import { parseCurrency } from '@/utils/parse-currency';

import { HiringGoalDetails } from './hiring-goal-details';
import { HiringGoal } from './types';

/* eslint-disable camelcase */
type Inputs = {
  budget_allotted?: number;
  expected_conversion?: number;
  expected_cpa?: number;
};
/* eslint-enable camelcase */

interface FormProps {
  hiringGoal: HiringGoal;
  children: React.ReactNode;
  onFormChange: (enabled: boolean) => void;
  showSpinner: (isSaving: boolean) => void;
}

export function Form({
  children,
  onFormChange,
  hiringGoal,
  showSpinner,
}: FormProps) {
  const intl = useIntl();
  const { notifySuccess, notifyError } = useToaster();
  const { refetch, targets, closeEditTargetsDialog } = useEditTargetsContext();

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    getValues,
    watch,
  } = useForm<Inputs>({
    defaultValues: {
      budget_allotted: parseCurrency(targets?.budget) || undefined,
      expected_cpa: parseCurrency(targets?.cpa) || undefined,
      expected_conversion: targets?.conversion ?? undefined,
    },
  });

  const targetConversion = watch('expected_conversion');
  const values = watch();

  useEffect(() => {
    // Enable submit button only when the form is dirty
    onFormChange(isDirty);
  }, [dirtyFields, isDirty, onFormChange, values]);

  const { mutate: updateTargets, isPending } = useMutation({
    mutationFn: updateHiringGoalTargets,
    onSuccess: async () => {
      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Targets updated successfully!',
        }),
      });

      closeEditTargetsDialog();
      if (refetch) {
        await refetch();
      }
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to update targets!',
        }),
      });
    },
  });

  useEffect(() => {
    showSpinner(isPending);
  }, [isPending, showSpinner]);

  const submitForm = () => {
    // Get only changed fields
    const changedFields = Object.keys(dirtyFields).reduce(
      (acc, key) => ({
        ...acc,
        [key]: getValues(key as keyof Inputs),
      }),
      {},
    );

    updateTargets({
      openingSlug: hiringGoal.openingSlug,
      requestBody: changedFields,
    });
  };

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <div className="flex flex-col gap-6 px-6 py-4">
        <HiringGoalDetails hiringGoal={hiringGoal} />

        <Input
          isNumber
          label={intl.formatMessage({
            defaultMessage: 'Target conversion rate',
          })}
          {...register('expected_conversion', {
            valueAsNumber: true,
            min: {
              value: 0,
              message: intl.formatMessage({
                defaultMessage: 'Conversion rate must be at least 0%',
              }),
            },
            max: {
              value: 100,
              message: intl.formatMessage({
                defaultMessage: 'Conversion rate must not exceed 100%',
              }),
            },
          })}
          placeholderLabel="%"
          placeholder={intl.formatMessage({
            defaultMessage: 'Enter target conversion rate',
          })}
          error={errors.expected_conversion?.message}
        />
        <TargetDetails
          title={intl.formatMessage({ defaultMessage: 'Target applicants' })}
          value={intl.formatMessage(
            {
              defaultMessage: '{target} applicants',
            },
            {
              target: targetConversion
                ? Math.round((hiringGoal.goal * 100) / targetConversion)
                : '--',
            },
          )}
        />

        <Input
          isNumber
          label={intl.formatMessage({ defaultMessage: 'Target budget' })}
          {...register('budget_allotted', { valueAsNumber: true })}
          placeholderLabel="$"
          placeholder={intl.formatMessage({
            defaultMessage: 'Enter target budget',
          })}
          error={errors.budget_allotted?.message}
        />

        <Input
          isNumber
          label={intl.formatMessage({ defaultMessage: 'Target CPA' })}
          {...register('expected_cpa', { valueAsNumber: true })}
          placeholderLabel="$"
          placeholder={intl.formatMessage({
            defaultMessage: 'Enter target CPA',
          })}
          error={errors.expected_cpa?.message}
        />
      </div>

      {/* Footer with submit button is passed as a child here */}
      {children}
    </form>
  );
}
